// src/plugins/sales-forecast/components/PriceRangeControl.tsx
import React from "react";
import {
  RangeSliderControl,
  RangeSliderControlProps,
} from "./RangeSliderControl";
import { useSystemTheme } from "../hooks/useSystemTheme";
import "../../sales-forecast/styles/theme.css";

export interface PriceRangeControlProps
  extends Pick<
    RangeSliderControlProps,
    "min" | "max" | "step" | "values" | "onChange"
  > {}

/**
 * Theme-aware PriceRangeControl — updates dynamically based on system light/dark mode.
 */
export const PriceRangeControl: React.FC<PriceRangeControlProps> = ({
  min,
  max,
  step,
  values,
  onChange,
}) => {
  const theme = useSystemTheme();

  // Pull system theme colors from CSS variables
  const css = getComputedStyle(document.documentElement);
  const textColor = css.getPropertyValue("--text-color").trim();
  const accentColor = css.getPropertyValue("--accent-color").trim();
  const borderColor = css.getPropertyValue("--border-color").trim();
  const mutedTextColor =
    css.getPropertyValue("--muted-text-color").trim() || textColor;

  // Format large numbers like 687500 → $687.5k
  const formatValue = (value: number) => {
    const abbreviated = (value / 1000).toFixed(1).replace(/\.0$/, "");
    return `$${abbreviated}k`;
  };

  return (
    <div
      className={`theme-${theme} transition-all duration-300`}
      style={{
        color: textColor,
      }}
    >
      <RangeSliderControl
        label="Price Range"
        min={min}
        max={max}
        step={step}
        values={values as [number, number]}
        onChange={onChange}
        formatValue={formatValue}
      />

      {/* Scoped CSS variables for theme-aware label and slider */}
      <style>{`
        .theme-${theme} .range-slider-label {
          color: ${textColor} !important;
          font-weight: 600;
        }
        .theme-${theme} .range-slider-track {
          background-color: ${borderColor};
        }
        .theme-${theme} .range-slider-thumb {
          background-color: ${accentColor};
        }
        .theme-${theme} .range-slider-value {
          color: ${mutedTextColor};
        }
      `}</style>
    </div>
  );
};